const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Integer cents (as a string, from the API) to a currency amount. Never via floats for the cents. */
export function money(cents: string | number | null | undefined, currency = "USD"): string {
  if (cents === null || cents === undefined) return "—";
  const v = BigInt(cents);
  const neg = v < 0n;
  const a = neg ? -v : v;
  const whole = (a / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const frac = (a % 100n).toString().padStart(2, "0");
  const sym =
    currency === "USD" ? "$" : currency === "EUR" ? "€" : currency === "GBP" ? "£" : `${currency} `;
  return `${neg ? "-" : ""}${sym}${whole}.${frac}`;
}

/** Short form for big headline numbers: $12,400 (no cents when whole). */
export function moneyShort(cents: string | number | null | undefined, currency = "USD"): string {
  const s = money(cents, currency);
  return s.endsWith(".00") ? s.slice(0, -3) : s;
}

export function periodName(key: string, long = false): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (m) return `${(long ? LONG : MONTHS)[Number(m[2]) - 1]} ${m[1]}`;
  const q = /^(\d{4})-Q([1-4])$/.exec(key);
  return q ? `Q${q[2]} ${q[1]}` : key;
}

export function date(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const x = typeof d === "string" ? new Date(d.length === 10 ? `${d}T00:00:00Z` : d) : d;
  return `${MONTHS[x.getUTCMonth()]} ${x.getUTCDate()}, ${x.getUTCFullYear()}`;
}

export function dateTime(d: string | Date | null | undefined): string {
  if (!d) return "—";
  const x = typeof d === "string" ? new Date(d) : d;
  return `${date(x)}, ${String(x.getUTCHours()).padStart(2, "0")}:${String(x.getUTCMinutes()).padStart(2, "0")} UTC`;
}

/** Whole days from today (UTC) to a deadline's last day: 0 means due today. */
export function daysLeft(deadline: string): number {
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((Date.parse(`${deadline.slice(0, 10)}T00:00:00Z`) - today) / 86_400_000);
}

export function minutes(m: string | number): string {
  const v = Number(m);
  if (v < 120) return `${trim(v)} min`;
  const h = Math.floor(v / 60);
  const r = v - h * 60;
  return r ? `${h} h ${trim(r)} min` : `${h} h`;
}

const trim = (v: number) => (Number.isInteger(v) ? String(v) : v.toFixed(1));

export const IMPACT: Record<string, string> = {
  major_outage: "Major outage",
  partial_outage: "Partial outage",
  degraded: "Degraded",
  maintenance: "Maintenance",
};

export const SOURCE: Record<string, string> = {
  status_page: "Vendor status page",
  cloud_health: "Vendor's health events for your account",
  check: "recoup check",
  email: "Vendor email",
  manual: "Added by your team",
};

export const STRENGTH: Record<string, string> = {
  vendor_acknowledged: "Confirmed by the vendor",
  buyer_observed: "Seen by recoup's checks",
  buyer_reported: "Reported by your team",
};

export const CLAIM_STATUS: Record<string, string> = {
  draft: "Draft",
  ready: "Ready to send",
  sent: "Sent",
  acknowledged: "Acknowledged",
  credited: "Credited",
  partially_credited: "Partly credited",
  disputed: "Disputed",
  rejected: "Rejected",
  expired: "Deadline passed",
};

export const REASON: Record<string, string> = {
  outage: "Outage",
  maintenance_unannounced: "Maintenance without notice",
  maintenance_short_notice: "Maintenance with too little notice",
  maintenance_notice_unverified: "Maintenance whose notice recoup couldn't confirm",
  maintenance_not_excluded: "Maintenance (the contract doesn't exclude it)",
  maintenance_over_allowance: "Maintenance beyond the monthly allowance",
  scheduled_maintenance: "Scheduled maintenance with notice",
  force_majeure: "Force majeure",
  customer_caused: "Caused by your team",
  third_party: "Third party",
};
